import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Checkbox, FormControlLabel, Grid, Tooltip } from '@mui/material';
import { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { ActionType, ResourceType } from '../../common/PricePrgClient';
import { RootState } from '../../store';
import PricelistTree from '../pricelist/PricelistTree';
import { PricelistTreeItem } from '../pricelist/pricelistTypes';
import { getPricelistPermissionsFromSelectedGroup } from '../pricelist/redux/pricelistSelectors';
import { getSelectedUserGroupId } from './redux/userGroupSelectors';
import { setPricelistRecordPermissionsForGroupRequest } from './redux/userGroupSlices';
const mapStateToProps = (state: RootState) => ({
    selectedUserGroupId: getSelectedUserGroupId(state),
    pricelistPermissions: getPricelistPermissionsFromSelectedGroup(state)
});

type Props = ReturnType<typeof mapStateToProps>;

const ManageGroupPricelistPermissions = ({ selectedUserGroupId, pricelistPermissions }: Props) => {
    const dispatch = useDispatch();

    const [filter, setFilter] = useState('');

    if (selectedUserGroupId == null) {
        return null;
    }

    const handlePermissionSet = (
        pricelistId: number,
        actionType: ActionType,
        hasPermission: boolean,
        applyToChildren: boolean
    ) => {
        dispatch(
            setPricelistRecordPermissionsForGroupRequest({
                recordId: pricelistId,
                resourceType: ResourceType.Pricelist,
                userGroupId: selectedUserGroupId,
                actionType: actionType,
                hasPermission: hasPermission,
                applyToChildren: applyToChildren
            })
        );
    };

    const treeItemRenderer = (item: PricelistTreeItem) => {
        const pricelistPermission = pricelistPermissions[item.id];
        let canRead: boolean;
        let canWrite: boolean;
        let canDelete: boolean;
        if (pricelistPermission != null) {
            canRead = pricelistPermission.canRead;
            canWrite = pricelistPermission.canUpdate;
            canDelete = pricelistPermission.canDelete;
        } else {
            canRead = false;
            canWrite = false;
            canDelete = false;
        }

        return (
            <Grid container alignItems="baseline" justifyContent="space-between">
                <Grid item xs={8}>
                    <span>{item.displayName}</span>
                </Grid>

                <Grid
                    item
                    xs={4}
                    sx={[
                        {
                            display: 'flex',
                            justifyContent: 'end'
                        },
                        {
                            '& .MuiCheckbox-root': {
                                paddingTop: 0,
                                marginTop: 0
                            }
                        }
                    ]}
                >
                    <Checkbox
                        checked={canRead}
                        onContextMenu={event => {
                            handlePermissionSet(item.id, ActionType.Read, !canRead, false);
                            event.preventDefault();
                        }}
                        onChange={(event, checked) =>
                            handlePermissionSet(item.id, ActionType.Read, checked, true)
                        }
                        color="primary"
                    />
                    <Checkbox
                        checked={canWrite}
                        onContextMenu={event => {
                            handlePermissionSet(item.id, ActionType.Update, !canWrite, false);
                            event.preventDefault();
                        }}
                        onChange={(event, checked) =>
                            handlePermissionSet(item.id, ActionType.Update, checked, true)
                        }
                        color="primary"
                    />
                    <Checkbox
                        checked={canDelete}
                        onContextMenu={event => {
                            handlePermissionSet(item.id, ActionType.Delete, !canDelete, false);
                            event.preventDefault();
                        }}
                        onChange={(event, checked) =>
                            handlePermissionSet(item.id, ActionType.Delete, checked, true)
                        }
                        color="primary"
                    />
                </Grid>
            </Grid>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '450px'
            }}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        checked={pricelistPermissions[0]?.canCreate ?? false}
                        onChange={(event, checked) =>
                            handlePermissionSet(0, ActionType.Create, checked, false)
                        }
                        color="primary"
                    />
                }
                label="Can read all and create new pricelists"
            />
            <PricelistTree
                multiSelect={false}
                treeItemRenderer={treeItemRenderer}
                header={
                    <Box
                        component="div"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            paddingRight: '18px'
                        }}
                    >
                        <Box component="span" sx={{ paddingRight: '18px' }}>
                            <Tooltip title="Permission to view">
                                <VisibilityIcon color="primary" />
                            </Tooltip>
                        </Box>
                        <Box component="span" sx={{ paddingRight: '18px' }}>
                            <Tooltip title="Permission to write">
                                <CreateIcon color="primary" />
                            </Tooltip>
                        </Box>
                        <Box component="span">
                            <Tooltip title="Permission to delete">
                                <DeleteIcon color="secondary" />
                            </Tooltip>
                        </Box>
                    </Box>
                }
                filter={filter}
                setFilter={setFilter}
            />
        </Box>
    );
};
export default connect(mapStateToProps)(ManageGroupPricelistPermissions);
